/**
 * Created by ren on 01/06/2017.
 */
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import groupBy from 'lodash/groupBy';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import keyBy from 'lodash/keyBy';
import { delay, takeEvery, select, call, put, takeLatest, all } from 'redux-saga/effects';
import { change, initialize, SubmissionError } from 'redux-form';
import * as SagaCommon from '../sagasCommon';
import * as APICommon from '../../services/apiCommon';
import * as SagaType from '../../constants/constant_saga_types';
import * as ActionType from '../../constants/constant_action_types';
import { PreferenceForm, PreferenceFilterForm } from '../../constants/constant_form_names';
import * as PreferenceAPI from '../../services/preference/api_preference';
import * as UtilityPreference from '../../services/preference/utilty_preference';
import { lists as Lists } from '../../common/lists';
import { buildPreferenceFilters } from '../../services/preference/filter_preference';
import {
  ACCOUNT,
  OBJECT,
  NUMERIC,
  STRING,
  INHERIT,
  LOCATION
} from '../../constants/constant_preference_types';
import { getSettingsByCategory } from '../../components/admin/preferences/PreferenceSelector';
import { validate } from '../../components/admin/preferences/PreferenceValidation';
import { SUBSIDIARY } from '../../constants/constant_doc_types';

//USE THIS FUNCTION IF you WANT TO SORT BY LABEL
// function groupPreferences(preferences){
//   let sortedPreferences = sortBy(preferences, 'label');
//   sortedPreferences = sortBy(sortedPreferences, 'category');
//   const groupedPreferences = groupBy(sortedPreferences, 'category');
//   return groupedPreferences;
// }

function groupPreferences(preferences) {
  let sortedPreferences = sortBy(preferences, 'category');
  const groupedPreferences = groupBy(sortedPreferences, 'category');
  return groupedPreferences;
}

/**
 * Save Preference Watcher
 * @param feathersApp
 */
export function* savePreferenceSaga(feathersApp) {
  yield takeEvery(SagaType.SAVE_PREFERENCE, callSavePreference, feathersApp);
}

/**
 * Save Preference
 * @param feathersApp
 * @param action
 */
export function* callSavePreference(feathersApp, action) {
  try {
    const { intl, user, form } = yield select();
    const { data, formField } = action;

    data.type_id = data.pref_type === 'account' ? user.tid : data.type_id;

    const error = validate(data);
    if (error._error.length) {
      delete error._error;
      throw new SubmissionError(error);
    }

    const filterForm = form[`${PreferenceFilterForm}_${formField}`];
    const filterFormValues = filterForm.values;
    const settings = yield select(getSettingsByCategory, formField);

    const preferenceFormName = `${PreferenceForm}_${formField}`;

    yield put(change(preferenceFormName, 'isLoading', true));
    yield SagaCommon.showProgressSaveRecord();

    let preferencesData = [];
    data.list.forEach(preferences => {
      let preference = {
        id: preferences[`${data.pref_type}_id`],
        value: preferences[data.pref_type],
        setting: preferences.setting,
        setting_type: preferences.setting_type,
        pref_type: data.pref_type,
        type_id: data.type_id
      };

      const inherit = preference.value === 'inherit' || !(preference.value + '').length;

      // Something is forcing it to be a string value
      // if (preference.setting_type === BOOLEAN) {
      //   if (preference.value === 'true') preference.value = true;
      //   else if (preference.value === 'false') preference.value = false;
      // }

      if (inherit) {
        preference._deleted = true;
        if (preference.id) preferencesData.push(preference);
      } else {
        preferencesData.push(preference);
      }
    });

    if (preferencesData.length) {
      const resultCall = yield preferencesData.map(preference => {
        return call(PreferenceAPI.savePreference, feathersApp, { user, data: preference });
      });
      const result = yield all(resultCall);

      yield delay(3000);
      const filters = yield buildPreferenceFilters(filterFormValues, settings, user.tid);
      const { pref_type, type_id } = data;
      yield put({
        type: SagaType.FETCH_PREFERENCES_WITH_FILTER,
        filters,
        categoryForm: formField,
        pref_type,
        type_id
      });

      yield put({
        type: ActionType.SET_ALL_PREFERENCE_SETTING,
        preferences: preferencesData,
        intl
      });

      yield SagaCommon.doneSaveRecord(result, 'Preference was saved');
    }
    yield put(change(preferenceFormName, 'editMode', false));
    yield put(change(preferenceFormName, 'isLoading', false));
  } catch (error) {
    console.error(error);
    yield SagaCommon.showErrorSaveRecord(error);
  }
}

/**
 * Fetch List Preference Watcher
 * @param feathersApp
 */
export function* fetchListPreferenceSaga(feathersApp) {
  yield takeEvery(SagaType.FETCH_LIST_PREFERENCE, callFetchListPreference, feathersApp);
}

/**
 * Fetch List Preference
 * @param feathersApp
 * @param action
 */
export function* callFetchListPreference(feathersApp, action) {
  try {
    yield SagaCommon.showProgressDataById();
    const { user, intl } = yield select();
    const dataParams = {
      filters: {
        pref_types: [ACCOUNT],
        type_ids: [user.tid],
        requested_page: 1,
        rows_per_page: 100000,
      },
      list_type: 'preference'
    };

    const preferenceListData = yield call(APICommon.getDocs, feathersApp, dataParams);
    const preferenceList = Lists.PREFERENCE_SETTING(intl);
    const preferenceData = (preferenceListData.data || []).filter(
      ({ setting }) => preferenceList.find(({ key }) => key === setting)
    );

    const preferences = yield call(buildPreferenceContent, preferenceData, intl);
    const namedPreferences = yield call(attachObjectReferences, feathersApp, preferences);
    const groupedPreferences = yield call(groupPreferences, namedPreferences);
    yield put({ type: ActionType.SET_PREFERENCES_CONTENT, preferences: groupedPreferences });
    yield SagaCommon.doneDataById();
  } catch (error) {
    yield SagaCommon.showErrorDataById(error);
  }
}

export function getPreferencesTypeIds(preferences) {
  let typeIds = [];
  preferences.forEach(preference => {
    if (preference.type_id) {
      typeIds.push(preference.type_id);
    }
  });

  return uniq(typeIds) || [];
}

/**
 * Fetch By ID Preference Watcher
 * @param feathersApp
 */
export function* fetchByIdPreferenceSaga(feathersApp) {
  yield takeEvery(SagaType.FETCH_PREFERENCE_BY_ID, callFetchByIdPreference, feathersApp);
}

/**
 * Fetch By ID Preference
 * @param feathersApp
 * @param action
 */
export function* callFetchByIdPreference(feathersApp, action) {
  try {
    const { id, silent } = action;

    yield SagaCommon.showProgressDataById(silent);

    const result = yield call(PreferenceAPI.fetchPreferenceById, feathersApp, { id });
    yield SagaCommon.doneDataById(result);
  } catch (error) {
    yield SagaCommon.showErrorDataById(error);
  }
}

/**
 * Fetch Preference With Filter Watcher
 * @param feathersApp
 */
export function* fetchPreferencesWithFilterSaga(feathersApp) {
  yield takeEvery(
    SagaType.FETCH_PREFERENCES_WITH_FILTER,
    callFetchPreferencesWithFilter,
    feathersApp
  );
}

/**
 * Fetch Preference With Filter
 * @param feathersApp
 * @param action
 */
export function* callFetchPreferencesWithFilter(feathersApp, action) {
  try {
    const { intl } = yield select();
    const { filters, categoryForm, pref_type, type_id } = action;
    const form = `${PreferenceForm}_${categoryForm}`;
    yield put(change(form, 'isLoading', true));
    const dataParams = {
      filters: {
        ...filters,
        requested_page: 1,
        rows_per_page: 100000,
      },
      list_type: 'preference'
    };
    const preferenceListData = yield call(APICommon.getDocs, feathersApp, dataParams);
    const preferences = yield call(buildPreferenceContent, preferenceListData.data, intl);
    const namedPreferences = yield call(attachObjectReferences, feathersApp, preferences);
    const groupedPreferences = yield call(groupPreferences, namedPreferences);
    const categoryPreferences = groupedPreferences[categoryForm];
    const initialValues = { [categoryForm]: categoryPreferences, pref_type, type_id };
    yield put(initialize(`${PreferenceForm}_${categoryForm}`, initialValues));
    yield put(change(form, 'isLoading', false));
  } catch (error) {
    yield SagaCommon.showErrorDataById(error);
  }
}

export function* buildPreferenceContent(preferences, intl) {
  try {
    const groupedPreferences = groupBy(preferences, 'pref_type');
    const defaultSettings = yield call(buildPreferenceSettings, intl);
    const updatedWithAccounts = yield call(
      setPreferenceToType,
      defaultSettings,
      'account',
      groupedPreferences.account
    );
    const updatedWithSubsidiaries = yield call(
      setPreferenceToType,
      updatedWithAccounts,
      'subsidiary',
      groupedPreferences.subsidiary
    );
    const updatedWithLocations = yield call(
      setPreferenceToType,
      updatedWithSubsidiaries,
      'location',
      groupedPreferences.location
    );
    return updatedWithLocations;
  } catch (error) {
    console.error(error);
    return [];
  }
}

export function buildPreferenceSettings(intl) {
  try {
    let defaultSettings = [];
    for (let defaultSetting of Lists.PREFERENCE_SETTING(intl)) {
      const preference = {
        setting: defaultSetting.key,
        setting_type: defaultSetting.type,
        account: '',
        subsidiary: '',
        location: '',
        applicable_to_account: defaultSetting.account,
        applicable_to_subsidiary: defaultSetting.subsidiary,
        applicable_to_location: defaultSetting.location,
        label: defaultSetting.label,
        category: defaultSetting.category,
        list: defaultSetting.list,
        filter: defaultSetting.filter,
        validation: defaultSetting.validation,
        doc_display: defaultSetting.doc_display,
        defined_list: defaultSetting.defined_list
      };
      defaultSettings.push(preference);
    }
    return defaultSettings;
  } catch (error) {
    console.error(error);
    return [];
  }
}

export function setPreferenceToType(defaultSettings, preferenceType, preferences) {
  try {
    const list = [...defaultSettings];
    for (let preferenceSetting of list) {
      let preference = find(preferences, { setting: preferenceSetting.setting });
      const index = findIndex(list, { setting: preferenceSetting.setting });

      if (preference) {
        preferenceSetting[`${preferenceType}_id`] = preference.id || preference._id;
        preferenceSetting[preferenceType] = preference.value;
        preferenceSetting.setting = preference.setting;
      } else {
        preferenceSetting[`${preferenceType}_id`] = null;
        const settingType = preferenceSetting.setting_type;
        if (settingType !== NUMERIC && settingType !== STRING && settingType !== OBJECT) {
          preferenceSetting[preferenceType] = 'inherit';
        }
      }
      list[index] = preferenceSetting;
    }
    return list;
  } catch (error) {
    console.error(error);
    return [];
  }
}

/**
 * Fetch Preference With Filter Watcher
 * @param feathersApp
 */
export function* fetchPreferenceSettingsSaga(feathersApp) {
  yield takeEvery(SagaType.FETCH_PREFERENCES_SETTINGS, callFetchPreferenceSettings, feathersApp);
}

/**
 * Fetch Preference With Filter
 * @param feathersApp
 * @param action
 */
export function* callFetchPreferenceSettings(feathersApp, action) {
  try {
    const {
      user,
      intl,
      subsidiary: {
        primary_subsidiary: { id }
      }
    } = yield select();
    const { settings, subsidiaryId = id, locationId } = action;
    const accountId = user.tid;
    const params = { settings, subsidiaryId, locationId, accountId, intl, user };
    const preferences = yield call(UtilityPreference.getPreferenceValues, feathersApp, params);
    yield put({ type: ActionType.SET_PREFERENCE_SETTING, settings: preferences });
  } catch (error) {
    console.error(error);
  }
}

/**
 * Fetch List Preference Watcher
 * @param feathersApp
 */
export function* fetchAllPreferenceSettingsSaga(feathersApp) {
  yield takeLatest(SagaType.FETCH_ALL_PREFERENCE, callFetchAllPreferenceSettings, feathersApp);
}

/**
 * Fetch All Preference Settings
 * @param feathersApp
 */
export function* callFetchAllPreferenceSettings(feathersApp) {
  try {
    const { intl } = yield select();
    const dataParams = {
      filters: {
        pref_types: [ACCOUNT, LOCATION, SUBSIDIARY],
        requested_page: 1,
        rows_per_page: 100000,
      },
      list_type: 'preference'
    };
    const preferences = yield call(APICommon.getDocs, feathersApp, dataParams);
    if (preferences.data) {
      yield put({
        type: ActionType.SET_ALL_PREFERENCE_SETTING,
        preferences: preferences.data,
        intl
      });
    }
  } catch (error) {
    console.error(error);
  }
}

export function* attachObjectReferences(feathersApp, preferences) {
  try {
    const objectPreferenceNames = yield call(PreferenceAPI.getDocPreferences, feathersApp, {
      preferences
    });
    const indexedObjectPreferences = keyBy(objectPreferenceNames, 'id');

    return preferences.map(preference => {
      const field = preference.doc_display || 'name';
      const defaultName = {};
      defaultName[field] = undefined;

      if (preference.setting_type === OBJECT) {
        if (preference.account) {
          const account = indexedObjectPreferences[preference.account] || defaultName;
          preference.account_name = account[field];
        }

        if (preference.subsidiary) {
          const subsidiary = indexedObjectPreferences[preference.subsidiary] || defaultName;
          preference.subsidiary_name = subsidiary[field];
        }

        if (preference.location && preference.location !== INHERIT) {
          const location = indexedObjectPreferences[preference.location] || defaultName;
          preference.location_name = location[field];
        }
      }
      return preference;
    });
  } catch (e) {
    console.error(e.stack);
  }
}
